/**
 * The /app area's two reads, held in memory for the session tab
 * (docs/agent-locations-rebuild.md 9.1).
 *
 *   overview  gbp-agent-portal app_overview: the account, its workspaces and
 *             locations. useAppAccount reads through it, so moving between
 *             /app pages renders at once.
 *   stats     gbp-agent-portal app_location_stats for the whole account (one
 *             call, capped at 500 by the op), plus one id refreshes for the
 *             settings pop-up and the push key it needs.
 *
 * Stale while revalidate (spec Addendum A, R04 to R07): every mount of a page
 * shows what is held at once AND reads again in the background (a read that
 * is already running is shared, and one that answered in the last
 * REVALIDATE_GAP_MS is used as is). invalidateAppData() is for the flows that
 * change the account outside /app (connect, reconnect) on their way back. A
 * failed background read keeps what is on screen and says so quietly
 * (refreshFailed); the full error shows only when there is nothing to show.
 *
 * A page that stays mounted reads again too (post-release fix T3, 01 Oct 26):
 * when its tab comes back into view or the browser restores it from the
 * back-forward cache, once what it holds is RETURN_GAP_MS old (usePageReturn).
 * A location connected in another tab (or on the way back from Google, whose
 * sign-in is a full page load) used to stay missing from an /app or
 * /app/connections already open, while its home said "Setting up your agent":
 * every app_overview read in that window listed it, but the open page never
 * asked. While the overview holds a location the agent is still setting up,
 * useAppAccount also reads every SETUP_POLL_MS so its state moves on.
 *
 * Memory only: the stats rows carry Google account emails, so nothing here is
 * written to sessionStorage or localStorage. Both are keyed by the signed-in
 * user and dropped on sign out (clearAppCaches, which AppShell calls, and an
 * auth listener here for every other sign out: the agent page menus, another
 * tab). A first render only shows a cached answer for the user the last
 * getSession or auth event named, never one that belongs to someone else.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fnErrorText } from '@/lib/fnError';
import { appSlugViewer, rememberAppSlugs, resetAppSlugs } from '@/lib/appAddress';
import { resetAspectHrefs } from '@/components/gbp-agent/agentLinks';
import { resetPageLinks } from '@/components/agent-pages/pageLinks';
import type { AppMember, AppOverview, AppWorkspace } from './appModel';
import type { LocationStatsAnswer, StatsRow } from './statsModel';

/** How long an answer is used without asking again (fetchOverview(false), a view with nothing mounting). */
export const APP_CACHE_TTL_MS = 60_000;

/** A mount right after a read (two parts of one page, a page and the shell) does not read again. */
export const REVALIDATE_GAP_MS = 2_000;

/** A page coming back into view reads again once what it holds is this old (T3). */
export const RETURN_GAP_MS = 10_000;

/** While a location is being set up, an open page reads the overview this often (T3). */
export const SETUP_POLL_MS = 30_000;

/**
 * Calls onReturn when the page comes back into view: its tab is shown again
 * (visibilitychange to visible) or the browser restores it from the
 * back-forward cache (pageshow persisted). Each caller decides whether what it
 * holds is old enough to read again.
 */
export function usePageReturn(onReturn: () => void, enabled = true): void {
  const ref = useRef(onReturn);
  ref.current = onReturn;
  useEffect(() => {
    if (!enabled || typeof document === 'undefined' || typeof window === 'undefined') return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') ref.current();
    };
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) ref.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onShow);
    };
  }, [enabled]);
}

const OVERVIEW_FALLBACK = 'Check your connection and try again.';
const STATS_FALLBACK = 'The numbers could not be loaded. Try again in a moment.';

type Listener = () => void;

/** The session user the last getSession or auth event named; null until known, or signed out. */
let knownUser: string | null = null;
let authWatched = false;

/** The signed-in user as far as this tab knows right now, for a first render. */
export function knownSessionUser(): string | null {
  return knownUser;
}

/**
 * Sign out, or a different user signed in (here or in another tab): nothing
 * cached for the old user stays. Other events with no session are ignored, so
 * a token refresh never empties the screen.
 */
function watchAuth(): void {
  if (authWatched) return;
  authWatched = true;
  try {
    supabase.auth.onAuthStateChange((event, session) => {
      const id = session?.user?.id ?? null;
      if (event === 'SIGNED_OUT') {
        console.log('[appData] signed out, dropping the app caches');
        knownUser = null;
        clearAppCaches();
        return;
      }
      if (!id) return;
      knownUser = id;
      if ([overview.userId, stats.userId, pushKey?.userId ?? null, appSlugViewer()].some((held) => held !== null && held !== id)) {
        console.log('[appData] a different user signed in, dropping the app caches');
        clearAppCaches();
      }
    });
  } catch (err) {
    // A test double of the client without auth events: the getSession check below still keys the caches.
    console.warn('[appData] could not listen for sign out', err);
  }
}

/** The signed-in user's id, or null when signed out. */
export async function sessionUserId(): Promise<string | null> {
  watchAuth();
  const { data } = await supabase.auth.getSession();
  knownUser = data.session?.user?.id ?? null;
  return knownUser;
}

/* -------------------------------------------------------------- overview */

interface OverviewCache {
  userId: string | null;
  value: AppOverview | null;
  /** When the value was read; 0 marks it stale (invalidated). */
  at: number;
  inflight: Promise<OverviewAnswer> | null;
  /** Bumped by every invalidation, so a read started before a write is not taken as fresh. */
  epoch: number;
}

const overview: OverviewCache = { userId: null, value: null, at: 0, inflight: null, epoch: 0 };
const overviewListeners = new Set<Listener>();

export type OverviewAnswer =
  | { kind: 'signed_out' }
  | { kind: 'ok'; userId: string; value: AppOverview }
  | { kind: 'error'; userId: string; error: string };

/** The cached overview, for the given user when one is named. */
export function getCachedOverview(userId?: string | null): AppOverview | null {
  if (userId !== undefined && userId !== overview.userId) return null;
  return overview.value;
}

/** The user the cached overview belongs to. */
export function cachedOverviewUser(): string | null {
  return overview.userId;
}

function overviewFresh(): boolean {
  return !!overview.value && overview.at > 0 && Date.now() - overview.at < APP_CACHE_TTL_MS;
}

/** True when the held overview is at least `ms` old, invalidated, or missing: a returning page reads again. */
export function overviewOlderThan(ms: number): boolean {
  return !overview.value || overview.at === 0 || Date.now() - overview.at >= ms;
}

/** Hear about a new overview answer (another page's reload, an invalidation). */
export function subscribeOverview(fn: Listener): () => void {
  overviewListeners.add(fn);
  return () => {
    overviewListeners.delete(fn);
  };
}

function emitOverview(): void {
  for (const fn of [...overviewListeners]) fn();
}

/**
 * cache: the held answer while it is fresh, else one read.
 * revalidate: a read unless one is running (shared) or one answered in the last REVALIDATE_GAP_MS.
 * force: a read whatever the age.
 */
export type ReadMode = 'cache' | 'revalidate' | 'force';

/**
 * The overview for the signed-in user: the cached answer while it is fresh,
 * else one read (shared by every caller that asks while it runs). force reads
 * again whatever the age.
 */
export function fetchOverview(force = false): Promise<OverviewAnswer> {
  return readOverview(force ? 'force' : 'cache');
}

/** The read every page mount makes (R04): a held answer still shows meanwhile. */
export function revalidateOverview(): Promise<OverviewAnswer> {
  return readOverview('revalidate');
}

function overviewJustRead(): boolean {
  return !!overview.value && overview.at > 0 && Date.now() - overview.at < REVALIDATE_GAP_MS;
}

async function readOverview(mode: ReadMode): Promise<OverviewAnswer> {
  const force = mode === 'force';
  const userId = await sessionUserId();
  if (!userId) {
    resetOverview();
    return { kind: 'signed_out' };
  }
  if (overview.userId !== userId) {
    resetOverview();
    overview.userId = userId;
  }
  // Another person's slugs never answer for this one (PR 1b).
  if (appSlugViewer() !== null && appSlugViewer() !== userId) resetAppSlugs();
  if (mode === 'cache' && overviewFresh() && overview.value) return { kind: 'ok', userId, value: overview.value };
  if (!force && overview.inflight) return overview.inflight;
  if (mode === 'revalidate' && overviewJustRead() && overview.value) return { kind: 'ok', userId, value: overview.value };

  const epoch = overview.epoch;
  const run = (async (): Promise<OverviewAnswer> => {
    const { data, error: fnError } = await supabase.functions.invoke('gbp-agent-portal', { body: { op: 'app_overview' } });
    if (fnError) {
      // The pages print their own "could not be loaded" title above this line,
      // so the fallback says what to do (copy audit, 29 Sep 26).
      return { kind: 'error', userId, error: await fnErrorText(fnError, OVERVIEW_FALLBACK) };
    }
    if (data?.error) return { kind: 'error', userId, error: String(data.error) };
    const value = data as AppOverview;
    // Every location it lists carries its clean address: the slug map the app's readers resolve /app/<slug> with.
    // Slugs are this person's own (PR 1b), so only while they are still the one signed in.
    if (overview.userId === userId) for (const ws of value?.workspaces ?? []) rememberAppSlugs(ws?.locations, userId);
    return { kind: 'ok', userId, value };
  })();
  overview.inflight = run;
  try {
    const answer = await run;
    // Signed out or someone else signed in while it ran: keep nothing.
    if (overview.userId !== userId) return answer;
    if (answer.kind === 'ok') {
      overview.value = answer.value;
      overview.at = overview.epoch === epoch ? Date.now() : 0;
      emitOverview();
    }
    return answer;
  } finally {
    if (overview.inflight === run) overview.inflight = null;
  }
}

/** After a write this session made: the next read asks again (the value stays on screen meanwhile). */
export function invalidateOverview(): void {
  overview.at = 0;
  overview.epoch += 1;
  overview.inflight = null;
}

function resetOverview(): void {
  overview.userId = null;
  overview.value = null;
  overview.at = 0;
  overview.inflight = null;
  overview.epoch += 1;
}

/* ----------------------------------------------------------------- stats */

interface StatsCache {
  userId: string | null;
  rows: Map<string, StatsRow>;
  /** When the last whole account answer came; 0 until one has. */
  at: number;
  /** invalidateAppData ran since that answer: the next view reads again (the rows stay on screen). */
  invalid: boolean;
  inflight: Promise<void> | null;
  /** The last whole account read failed (rows may still hold an older answer). */
  failed: string | null;
  /** The op stopped at 500 locations. */
  capped: boolean;
  /** Rows to read again on the next view (a pop-up save). */
  stale: Set<string>;
  epoch: number;
}

const stats: StatsCache = { userId: null, rows: new Map(), at: 0, invalid: false, inflight: null, failed: null, capped: false, stale: new Set(), epoch: 0 };
const statsListeners = new Set<Listener>();

/** The push key from a with_push call; undefined until one has been made. */
let pushKey: { userId: string; value: string | null } | null = null;

function emitStats(): void {
  for (const fn of [...statsListeners]) fn();
}

function statsFresh(): boolean {
  return stats.at > 0 && !stats.invalid && Date.now() - stats.at < APP_CACHE_TTL_MS;
}

function statsJustRead(): boolean {
  return stats.at > 0 && !stats.invalid && !stats.failed && Date.now() - stats.at < REVALIDATE_GAP_MS;
}

function claimStatsFor(userId: string): void {
  if (stats.userId === userId) return;
  resetStats();
  stats.userId = userId;
  if (pushKey && pushKey.userId !== userId) pushKey = null;
}

async function callStats(body: Record<string, unknown>): Promise<{ answer: LocationStatsAnswer | null; error: string | null }> {
  const { data, error: fnError } = await supabase.functions.invoke('gbp-agent-portal', { body: { op: 'app_location_stats', ...body } });
  if (fnError) return { answer: null, error: await fnErrorText(fnError, STATS_FALLBACK) };
  if (data?.error) return { answer: null, error: String(data.message ?? data.error) };
  return { answer: data as LocationStatsAnswer, error: null };
}

/** Read the whole account's stats (shared while it runs). */
async function loadAllStats(mode: ReadMode): Promise<void> {
  const force = mode === 'force';
  const userId = await sessionUserId();
  if (!userId) {
    resetStats();
    emitStats();
    return;
  }
  claimStatsFor(userId);
  if (mode === 'cache' && statsFresh() && !stats.failed) {
    if (stats.stale.size > 0) await refreshStale(userId);
    return;
  }
  if (!force && stats.inflight) return stats.inflight;
  if (mode === 'revalidate' && statsJustRead()) {
    if (stats.stale.size > 0) await refreshStale(userId);
    return;
  }
  const epoch = stats.epoch;
  const run = (async () => {
    const { answer, error } = await callStats({});
    if (stats.userId !== userId || stats.epoch !== epoch) return;
    if (answer) {
      stats.rows = new Map(answer.locations.map((r) => [r.location_id, r]));
      stats.capped = answer.capped === true;
      stats.at = Date.now();
      stats.invalid = false;
      stats.failed = null;
      stats.stale.clear();
    } else {
      stats.failed = error;
    }
    emitStats();
  })();
  stats.inflight = run;
  try {
    await run;
  } finally {
    if (stats.inflight === run) stats.inflight = null;
  }
}

/** The rows a pop-up save marked stale, in one call. */
async function refreshStale(userId: string): Promise<void> {
  const ids = [...stats.stale];
  if (!ids.length) return;
  const { answer } = await callStats({ location_ids: ids });
  if (!answer || stats.userId !== userId) return;
  for (const id of ids) stats.stale.delete(id);
  for (const r of answer.locations) stats.rows.set(r.location_id, r);
  emitStats();
}

export interface LocationStats {
  rows: ReadonlyMap<string, StatsRow>;
  /**
   * rows once an answer is in and the last read did not fail, else null. Pass
   * this (not rows) to stripTotals, insights, groupManage and connectionCounts,
   * which read null as "skeleton or Not loaded", never as zeros.
   */
  ready: ReadonlyMap<string, StatsRow> | null;
  /** No answer yet for this user. */
  loading: boolean;
  /** The read failed and there is nothing to show: the sentence to show beside "Try again". */
  failed: string | null;
  /**
   * A background read failed while an older answer is on screen (R06): a quiet "Could not refresh. Try again."
   * line. Always set by useLocationStats; optional only so the pages' own stand-ins type-check.
   */
  refreshFailed?: boolean;
  capped: boolean;
  reload: () => Promise<void>;
}

const NO_ROWS: ReadonlyMap<string, StatsRow> = new Map();

/** Only the known session user's rows; anything else reads as not loaded yet. */
function statsSnapshot(): Omit<LocationStats, 'reload'> {
  if (!knownUser || stats.userId !== knownUser) return { rows: NO_ROWS, ready: null, loading: true, failed: null, refreshFailed: false, capped: false };
  const answered = stats.at > 0;
  const loading = !answered && !stats.failed;
  return {
    rows: stats.rows,
    // R06: an older answer stays on screen when a later read fails.
    ready: answered ? stats.rows : null,
    loading,
    failed: answered ? null : stats.failed,
    refreshFailed: answered && !!stats.failed,
    capped: stats.capped,
  };
}

/** Nothing to read (A49: an account with no locations): no call, and nothing pending. */
const IDLE_STATS: Omit<LocationStats, 'reload'> = { rows: NO_ROWS, ready: NO_ROWS, loading: false, failed: null, refreshFailed: false, capped: false };

/**
 * Every location's stats for the signed-in account (spec 9.4). Each mount
 * shows what is held and reads again in the background (R04). enabled false
 * (A49: the overview has no locations, or is not in yet) makes no call at all;
 * the stats op answers 400 for an account with no workspace.
 */
export function useLocationStats(opts: { enabled?: boolean } = {}): LocationStats {
  const enabled = opts.enabled !== false;
  const [snap, setSnap] = useState(statsSnapshot);
  useEffect(() => {
    if (!enabled) return undefined;
    const on = () => {
      setSnap(statsSnapshot());
      // R07: the cache was cleared under this page (a different user signed in): read for the one now known.
      if (knownUser && stats.userId !== knownUser && !stats.inflight) void loadAllStats('revalidate');
    };
    statsListeners.add(on);
    setSnap(statsSnapshot());
    void loadAllStats('revalidate');
    return () => {
      statsListeners.delete(on);
    };
  }, [enabled]);
  // T3: a page that stays open reads again when it comes back into view, once its rows are RETURN_GAP_MS old.
  usePageReturn(() => {
    if (stats.at > 0 && !stats.invalid && !stats.failed && Date.now() - stats.at < RETURN_GAP_MS) return;
    console.log('[appData] page back in view, reading the stats again');
    void loadAllStats('revalidate');
  }, enabled);
  const reload = useCallback(() => loadAllStats('force'), []);
  return { ...(enabled ? snap : IDLE_STATS), reload };
}

/** One location's cached row for the known session user, or null. */
export function getStatsRow(locationId: string): StatsRow | null {
  if (!knownUser || stats.userId !== knownUser) return null;
  return stats.rows.get(locationId) ?? null;
}

/** The cached push key: undefined until a with_push call answered for the known session user. */
export function getPushKey(): string | null | undefined {
  return pushKey && pushKey.userId === knownUser ? pushKey.value : undefined;
}

/**
 * Read one location again (the settings pop-up), and the push key with it
 * when asked and not yet known. The row goes into the cache.
 */
export async function fetchStatsFor(
  locationId: string,
  opts: { withPush?: boolean } = {},
): Promise<{ row: StatsRow | null; push_public_key: string | null | undefined; error: string | null }> {
  const userId = await sessionUserId();
  if (!userId) return { row: null, push_public_key: undefined, error: 'Sign in again to see this location.' };
  claimStatsFor(userId);
  const askPush = opts.withPush === true && !pushKey;
  const { answer, error } = await callStats({ location_ids: [locationId], ...(askPush ? { with_push: true } : {}) });
  if (!answer) return { row: getStatsRow(locationId), push_public_key: getPushKey(), error };
  if (stats.userId !== userId) return { row: null, push_public_key: undefined, error: null };
  if (askPush) pushKey = { userId, value: answer.push_public_key ?? null };
  const row = answer.locations.find((r) => r.location_id === locationId) ?? null;
  if (row) stats.rows.set(locationId, row);
  stats.stale.delete(locationId);
  emitStats();
  return { row, push_public_key: getPushKey(), error: null };
}

/** A deleted location leaves the cache. */
export function dropStats(locationId: string): void {
  stats.rows.delete(locationId);
  stats.stale.delete(locationId);
  emitStats();
}

/** A save changed this location: read it again on the next view. */
export function staleStats(locationId: string): void {
  stats.stale.add(locationId);
}

/**
 * The account changed outside /app (R04, R05): the connect flow added a
 * location, a reconnect fixed a sign-in. Call it on the way back (a return
 * param or return_to): the next view of either read asks again, and what is
 * held stays on screen until the answer comes. A read already running started
 * before the change, so its answer is not kept as fresh.
 */
export function invalidateAppData(): void {
  invalidateOverview();
  stats.invalid = true;
  stats.inflight = null;
  stats.epoch += 1;
}

function resetStats(): void {
  stats.userId = null;
  stats.rows = new Map();
  stats.at = 0;
  stats.invalid = false;
  stats.inflight = null;
  stats.failed = null;
  stats.capped = false;
  stats.stale = new Set();
  stats.epoch += 1;
}

/** Sign out: forget both reads and the push key. */
export function clearAppCaches(): void {
  resetOverview();
  resetStats();
  // Slugs are per person (PR 1b): the last person's address book goes too,
  // and so do the page_links answers, which hold that person's /app/<slug>
  // addresses (clean addresses PR 2).
  resetAppSlugs();
  resetAspectHrefs();
  resetPageLinks();
  pushKey = null;
  emitOverview();
  emitStats();
}

/* ---------------------------------------------------------- permissions */

/** Owner or admin of the workspace: may delete its locations and reconnect Google. */
export function canManageWorkspace(ws: Pick<AppWorkspace, 'role' | 'members'> | null | undefined, userId: string | null | undefined): boolean {
  if (!ws || !userId) return false;
  if (ws.role === 'owner') return true;
  return (ws.members ?? []).some((m: AppMember) => m.user_id === userId && (m.role === 'owner' || m.role === 'admin'));
}
